import { useState, useEffect } from "react";
import { track } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquareHeart, Coins } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

interface FeedbackLoopModalProps {
  open: boolean;
  onRespond: () => void;
  onDismiss: () => void;
}

export function FeedbackLoopModal({ open, onRespond, onDismiss }: FeedbackLoopModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) track("feedback_modal_shown");
  }, [open]);

  const handleSend = async () => {
    if (!message.trim()) return;
    setLoading(true);
    try {
      const result = await api.post<{ creditsAwarded: boolean }>(
        "/newsletter/feedback",
        { message: message.trim() },
        user?.accessToken
      );
      track("feedback_submitted", { credits_awarded: result?.creditsAwarded ?? false });
      toast.success(
        result?.creditsAwarded ? t("feedbackModal.toastCredits") : t("feedbackModal.toastSuccess")
      );
    } catch {
      // Proceed even if the request fails — don't block the user
    } finally {
      setLoading(false);
      onRespond();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onDismiss();
  };

  const handleDismiss = () => {
    track("feedback_dismissed");
    onDismiss();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-easly-blue/10 flex items-center justify-center">
            <MessageSquareHeart className="w-6 h-6 text-easly-blue" />
          </div>
          <DialogTitle className="text-center text-xl font-display">
            {t("feedbackModal.title")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("feedbackModal.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <Coins className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <span>
            {t("feedbackModal.creditsHint.prefix")}{" "}
            <span className="font-semibold">{t("feedbackModal.creditsHint.reward")}</span>
            {t("feedbackModal.creditsHint.suffix")}
          </span>
        </div>

        <Textarea
          placeholder={t("feedbackModal.placeholder")}
          className="min-h-[120px] resize-none"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <Button
          className="w-full font-semibold h-11"
          onClick={handleSend}
          disabled={loading || !message.trim()}
        >
          {loading ? t("feedbackModal.sending") : t("feedbackModal.submit")}
        </Button>

        <button
          type="button"
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={handleDismiss}
        >
          {t("feedbackModal.maybeLater")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
